import React from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowUp, ArrowDown, CalendarClock, CreditCard } from "lucide-react";
import PlanFeatureGroups from "./PlanFeatureGroups";
import { getIncludedPlanFeatures } from "./planFeatureUtils";
import { describePlanChange, formatMoney, tierPriceFor } from "@/lib/billingCheckout";

const INTERVAL_LABELS = { month: "Monthly", year: "Yearly" };

const formatDate = (value) =>
  new Date(value).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

/**
 * Confirmation modal for changing subscription tier.
 * Paid tiers continue to the payment gateway (PayPal) for approval;
 * free tiers change immediately.
 */
export default function ChangeSubscriptionModal({
  open,
  onClose,
  selectedTier,
  currentTier,
  subscription,
  interval = "month",
  onIntervalChange,
  gateway,
  onConfirm,
  loading,
}) {
  const reason = "";

  if (!selectedTier) return null;

  const isUpgrade = selectedTier.sort_order > (currentTier?.sort_order ?? -1);
  const isDowngrade = !isUpgrade;
  const includedFeatures = getIncludedPlanFeatures(selectedTier);
  const isPaid = !selectedTier.is_free;
  const hasYearly = tierPriceFor(selectedTier, "year") != null;
  const effectiveInterval = interval === "year" && !hasYearly ? "month" : interval;
  const price = tierPriceFor(selectedTier, effectiveInterval);
  const priceLabel = price != null ? formatMoney(price, selectedTier.currency || "USD") : null;
  const timing = isPaid
    ? describePlanChange({ tier: selectedTier, interval: effectiveInterval, subscription })
    : null;
  const gatewayName = gateway?.display_name || "PayPal";
  const unavailableReason = !isPaid
    ? null
    : !gateway
      ? "Online payments aren't available right now. Please try again later."
      : price == null
        ? "This plan doesn't have a price for this billing period yet."
        : timing?.alreadySubscribed
          ? "You're already subscribed to this plan."
          : null;

  const handleConfirm = () => {
    onConfirm?.(selectedTier, reason, effectiveInterval);
  };

  const confirmLabel = loading
    ? isPaid
      ? `Opening ${gatewayName}...`
      : "Processing..."
    : isPaid
      ? `Continue to ${gatewayName}`
      : isUpgrade
        ? "Confirm Upgrade"
        : "Confirm Change";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isUpgrade ? "Upgrade Subscription" : "Change Plan"}
      maxWidthClass="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isUpgrade ? "default" : "secondary"}
            onClick={handleConfirm}
            disabled={loading || Boolean(unavailableReason)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Direction indicator */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/10">
          {isUpgrade ? (
            <ArrowUp className="w-8 h-8 text-success" />
          ) : (
            <ArrowDown className="w-8 h-8 text-yellow-500" />
          )}
          <div>
            <p className="text-sm text-textSecondary">
              {isUpgrade ? "Upgrading to" : "Switching to"}
            </p>
            <p className="text-lg font-semibold text-textPrimary">
              {selectedTier.name}
            </p>
          </div>
          <Badge variant={isUpgrade ? "success" : "warning"} className="ml-auto">
            {isUpgrade ? "Upgrade" : "Downgrade"}
          </Badge>
        </div>

        {/* Description */}
        {selectedTier.description && (
          <p className="text-sm text-textSecondary">{selectedTier.description}</p>
        )}

        {/* Billing period + price */}
        {isPaid && (
          <div className="space-y-3 rounded-lg border border-borderDefault p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-md border border-borderDefault p-0.5" role="group" aria-label="Billing period">
                {["month", "year"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={loading || (option === "year" && !hasYearly)}
                    onClick={() => onIntervalChange?.(option)}
                    className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-40 ${
                      effectiveInterval === option
                        ? "bg-primary text-white"
                        : "text-textSecondary hover:text-textPrimary"
                    }`}
                  >
                    {INTERVAL_LABELS[option]}
                  </button>
                ))}
              </div>
              {priceLabel && (
                <p className="text-lg font-semibold text-textPrimary">
                  {priceLabel}
                  <span className="text-sm font-normal text-textSecondary">
                    {" "}/ {effectiveInterval === "year" ? "year" : "month"}
                  </span>
                </p>
              )}
            </div>

            {priceLabel && timing && !timing.alreadySubscribed && (
              <div className="flex items-start gap-2 text-sm text-textSecondary">
                {timing.startsAt ? (
                  <CalendarClock className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                ) : (
                  <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                )}
                <p>
                  {timing.startsAt
                    ? `Your current plan stays active until ${formatDate(timing.startsAt)}. ${selectedTier.name} starts then, and ${gatewayName} charges ${priceLabel} on that date.`
                    : timing.replacesPaidPlan
                      ? `${selectedTier.name} starts today and ${gatewayName} charges ${priceLabel} now. The unused part of your current billing period isn't refunded.`
                      : `${gatewayName} charges ${priceLabel} today, then every ${effectiveInterval} until you cancel.`}
                </p>
              </div>
            )}

            {gateway?.environment === "sandbox" && (
              <Badge variant="warning">Sandbox mode: test payments only</Badge>
            )}
          </div>
        )}

        {unavailableReason && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm text-yellow-300">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
            <p>{unavailableReason}</p>
          </div>
        )}

        {/* Downgrade warning */}
        {isDowngrade && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-300">
              <p className="font-medium">Important</p>
              <p className="mt-1">
                Downgrading may reduce your feature limits. Your current usage
                data will be preserved, but quotas will be adjusted to match
                the new plan.
              </p>
            </div>
          </div>
        )}

        {/* Features */}
        <div>
          <h4 className="text-sm font-medium text-textPrimary mb-2">
            Plan includes:
          </h4>
          <PlanFeatureGroups features={includedFeatures} />
        </div>
      </div>
    </Modal>
  );
}
